import type { FC } from "react";
import { useState } from "react";

import { sendTelemetryEvent } from "@/lib/telemetry";
import type { IEntryPointComponent, IMasterResult } from "@/src/common/PageTransition";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import type { IMgmMainPage } from "@/src/member-get-member/MainPage/MgmMainPage";
import { MgmMainPageResources } from "@/src/member-get-member/MainPage/MgmMainPage.Resources";
import { MgmMainPageResourcesEn } from "@/src/member-get-member/MainPage/MgmMainPage.Resources.En";
import MgmUpdateCodeModal from "@/src/member-get-member/MainPage/MgmUpdateCodeModal";
import { useQueryClientCustom } from "@/src/query-client-overrides";
import { browserStatics } from "@/src/shared/BrowserStatic";
import ButtonV2 from "@Components/Button/ButtonV2";
import ImageLoader from "@Components/ImageLoader";
import {
    CommonEventType,
    MgmEventType,
    TelemetryEntity,
    copyTextToClipboard,
    getCdnImage,
    shareToNativeApi,
} from "@Components/Modules/Helpers";
import { TextboxAppearanceV2, TextboxV2 } from "@Components/Textbox";
import { Index as MgmIndex } from "@Routes/MemberGetMemberLinks";

import type { IMgmDashboard } from "./MgmMainPage.d";

import * as portalCss from "../../shared/Portal.module.css";
import * as css from "./MgmDashboard.module.css";

const MgmDashboard: FC<IMgmDashboard> = (props) => {
    const { dashboard: resourcesDashboard, changeCode: resourcesChangeCode } = languageSelector(
        MgmMainPageResources,
        MgmMainPageResourcesEn,
    );
    const { isMobileOrTablet } = useViewportInfoContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [mgmCodes, setMgmCodes] = useState({ code: props.voucherCode, link: props.voucherLink });
    const queryClient = useQueryClientCustom();

    const shareCode = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const shareText = resourcesDashboard.shareCodeMessage.replace(
            "{receiverBonus}",
            props.receiverBonusAmount,
        );

        sendTelemetryEvent({
            eventName: `${TelemetryEntity.Cashback}-${MgmEventType.mainDashboardCodeShare}`,
            actionType: "Click",
        });

        const appShared = shareToNativeApi(resourcesDashboard.shareCodeTitle, shareText, mgmCodes.link);
        if (appShared) {
            return;
        }

        if (isMobileOrTablet) {
            try {
                navigator.share({
                    title: resourcesDashboard.shareCodeTitle,
                    text: shareText,
                    url: mgmCodes.link,
                });
                return;
            } catch {
                console.warn("Share not available");
            }
        }

        copyTextToClipboard(mgmCodes.link);

        if (ev.currentTarget.classList.contains(css.copyClick) !== true) {
            ev.currentTarget.classList.add(css.copyClick);
            ev.currentTarget.innerText = resourcesDashboard.referralCopied;

            setTimeout(() => {
                const ele = ev.target as HTMLElement;

                ele.classList.remove(css.copyClick);
                ele.innerText = resourcesDashboard.referralBtnCopy;
            }, 1500);
        }
    };

    const bonusText = resourcesDashboard.bonusDescription
        .replace("{receiverBonus}", props.receiverBonusAmount)
        .replace("{senderBonus}", props.senderBonusAmount);

    return (
        <>
            <div className={`${css.mgmDashboardComponent} ${portalCss.standardContainerV2}`}>
                <div className={`${css.contentWrapper} ${portalCss.setSectionColOne}`}>
                    <div className={css.titleWrapper}>
                        <p
                            className={css.title}
                            dangerouslySetInnerHTML={{ __html: resourcesDashboard.title }}
                        />
                        <b className={css.subTitle}>{bonusText}</b>
                    </div>

                    <ImageLoader src={getCdnImage(process.env.MGM_MainImg)} alt="Goodie Member Get Member" />

                    <div className={css.inputsWrapper}>
                        <p
                            className={css.title}
                            dangerouslySetInnerHTML={{ __html: resourcesDashboard.inputTitle }}
                        />
                        <div className={css.inputItem}>
                            <p>{resourcesDashboard.referralLabel}</p>
                            <TextboxV2
                                appearance={TextboxAppearanceV2.Round}
                                value={mgmCodes.link}
                                readOnly
                            />
                            <ButtonV2 onClick={shareCode} appearance="magentaRoundAppearance">
                                {browserStatics.isMobileApp || isMobileOrTablet
                                    ? resourcesDashboard.referralBtnShare
                                    : resourcesDashboard.referralBtnCopy}
                            </ButtonV2>
                        </div>
                        <div className={css.inputItem}>
                            <p>{resourcesDashboard.codeLabel}</p>
                            <TextboxV2
                                appearance={TextboxAppearanceV2.Round}
                                value={mgmCodes.code}
                                autoComplete="off"
                                readOnly
                            />
                            <ButtonV2
                                appearance="transparentRoundMagentaBorderAppearance"
                                onClick={() => {
                                    setIsModalOpen(true);
                                    sendTelemetryEvent({
                                        eventName: `${TelemetryEntity.Cashback}-${MgmEventType.mainDashboardCodeEdit}`,
                                        actionType: "Click",
                                    });
                                }}
                            >
                                {resourcesDashboard.codeBtn}
                            </ButtonV2>
                        </div>
                    </div>
                </div>

                {isModalOpen && (
                    <MgmUpdateCodeModal
                        code={mgmCodes.code}
                        updateCode={(newCode: string) => {
                            setMgmCodes({
                                code: newCode,
                                link: props.voucherLink.replace(props.voucherCode, newCode),
                            });
                            setIsModalOpen(false);

                            queryClient.setQueryData(
                                [MgmIndex, true],
                                (oldData: IMasterResult<IMgmMainPage & IEntryPointComponent>) => {
                                    oldData.component.mgmUserDetails.voucherCode = newCode;
                                    oldData.component.mgmUserDetails.voucherLink = props.voucherLink.replace(
                                        props.voucherCode,
                                        newCode,
                                    );
                                    return oldData;
                                },
                            );
                        }}
                        modalClose={() => {
                            sendTelemetryEvent({
                                eventName: `${TelemetryEntity.Common}-${CommonEventType.exit}`,
                                actionType: "Click",
                            });
                            setIsModalOpen(false);
                        }}
                        resources={resourcesChangeCode}
                    />
                )}
            </div>
        </>
    );
};

export default MgmDashboard;
